import { inject, Injectable, InjectionToken, DOCUMENT } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, fromEvent, Observable, of, timer } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { PlatformInstanceListener } from '@/app/platform/platform-core/user/listeners/platform-instance-listener';

/** Zo lang mag de live verbinding weg zijn vóór de navbar het toont (een release duurt enkele seconden). */
export const LIVE_CONNECTION_GRACE_MS = 30_000;

/**
 * Bron van "de live verbinding is onbedoeld weg". Standaard de platform-hub: die start voor
 * elke bezoeker (ook op de loginpagina), en de andere hubs hangen aan dezelfde server.
 */
export const LIVE_CONNECTION_LOST$ = new InjectionToken<Observable<boolean>>('LIVE_CONNECTION_LOST$', {
  providedIn: 'root',
  factory: () => inject(PlatformInstanceListener).connectionLost$
});

/**
 * Vervangt de oude toasts bij elk wegvallen/herstellen van de verbinding: pas wanneer de
 * verbinding langer dan 30 s weg is TERWIJL de tab zichtbaar is, zet dit `connectionLost`
 * aan. Een verborgen tab verbreekt bewust (SignalRHubListener) en verbindt opnieuw bij
 * terugkeer; die tijd telt dus niet mee, de wachttijd start dan opnieuw.
 */
@Injectable({ providedIn: 'root' })
export class LiveConnectionStatusService {
  private readonly document = inject(DOCUMENT);
  private readonly lost$ = inject(LIVE_CONNECTION_LOST$);

  private readonly visible$ = fromEvent(this.document, 'visibilitychange').pipe(
    startWith(undefined),
    map(() => this.document.visibilityState === 'visible')
  );

  readonly connectionLost = toSignal(
    combineLatest([this.lost$, this.visible$]).pipe(
      map(([lost, visible]) => lost && visible),
      distinctUntilChanged(),
      switchMap(lostWhileVisible =>
        lostWhileVisible ? timer(LIVE_CONNECTION_GRACE_MS).pipe(map(() => true), startWith(false)) : of(false)
      ),
      distinctUntilChanged()
    ),
    { initialValue: false }
  );
}
